'use client';
import Image from 'next/image';
import DotCard from '@/components/content-types/dotCard';
import FeatureTable from '@/components/content-types/featureTable';
import { DotBlockEditor } from '../shared/decodeTextBlock';
import { IconCircleArrowUpRight, IconArrowNarrowRight, IconCircleCheck } from '@tabler/icons-react';
import { useEffect, useState, useRef } from 'react';
import { cn } from '@/app/utils/cn';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { decode } from 'html-entities';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';
import { Shield } from 'lucide-react';

function getImageUrl(image, width = 437) {
    return image?.identifier
        ? getDotcmsImageURL({
              src: image.identifier,
              width,
              raw: true
          })
        : undefined;
}

function createFeaturesToRender(feature) {
    return (
        feature?.map((item, index) => ({
            id: item.identifier || `feature-${index}`,
            title: item.title || item.headline || '',
            description: item.description || item.subheading || item.intro || '',
            image: item['_map'].image,
            altImage: item['_map'].altImage,
            body: item.body,
            afterBody: item.afterBody,
            url: item.callToAction?.[0]?.url || null,
            category: item.category?.[0] ? Object.keys(item['_map'].category[0])[0] : null,
            label: item.label || null
        })) || []
    );
}

function AccordionItem({ item, tableHeader }) {
    const [isOpen, setIsOpen] = useState(false);

    const toggleAccordion = () => {
        setIsOpen((prevState) => !prevState);
    };

    const features =
        item?.featuretablecells?.map((itemTable) => ({
            title: itemTable?.cellName,
            columnValueOne: itemTable?.columnValueOne,
            columnValueTwo: itemTable?.columnValueTwo,
            columnValueThree: itemTable?.columnValueThree,
            columnLabelFour: itemTable?.columnValueFour,
            description: itemTable?.description
        })) || [];

    return (
        <div>
            <button
                className="text-lg flex w-full items-center justify-start p-2 font-semibold text-blue-625 hover:cursor-pointer sm:px-14 sm:py-6"
                onClick={toggleAccordion}>
                <span
                    className={`flex size-8 items-center justify-center rounded-full border bg-white text-blue-625 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 bg-indigo-100' : ''
                    }`}>
                    {isOpen ? '-' : '+'}
                </span>
                <span className="pl-2 text-[10px] text-blue-700 sm:text-sm">
                    {item?.title} ({item?.featuretablecells?.length || 0})
                </span>
            </button>

            {isOpen && (
                <div>
                    <FeatureTable
                        alignCellCenter={tableHeader?.alignCellCenter}
                        markedAsList={tableHeader?.markedAsList}
                        tableFeatures={features}
                        isAccordionTable
                    />
                </div>
            )}
        </div>
    );
}

function ListItem(props) {
    const { node, specialStyle } = props;
    if (!node?.content) return null;

    const [item] = node?.content;
    const { content: itemContent } = item;
    const [{ text }] = itemContent;
    const style = specialStyle ? 'font-normal mb-3' : 'font-semibold';
    return <li className={cn('text-base dark:text-black', style)}>{decode(text)}</li>;
}

function Paragraph(props) {
    const { node, specialStyle, customStyle } = props;
    if (node?.content === undefined || node?.content === null) return null;

    const [{ marks, text }] = node?.content;

    const linkMark = marks?.find((mark) => mark.type === 'link');
    const linkAttributes = linkMark?.attrs;
    let style = '';
    if (customStyle) {
        style = customStyle;
    } else if (specialStyle) {
        style = 'font-normal text-2xl';
    } else {
        style = 'font-semibold';
    }
    return (
        <p className={cn('text-base dark:text-black', style)}>
            {linkAttributes ? (
                <LinkInParagraph url={linkAttributes.href} attributes={linkAttributes}>
                    {decode(text)}
                </LinkInParagraph>
            ) : (
                decode(text)
            )}
        </p>
    );
}

function LinkInParagraph({ url, attributes, children }) {
    return (
        <LinkNoPrefetch
            href={url}
            target={attributes?.target || '_self'}
            rel={attributes?.rel || ''}
            className={attributes?.class || 'text-section-color hover:underline'}>
            {children}
        </LinkNoPrefetch>
    );
}

function Layout01({ feature }) {
    const [open, setOpen] = useState(feature?.[0]?.identifier || null);

    const twClassesAccordion = cn('flex flex-col relative w-full bg-white rounded-lg relative');
    const twHeader = cn(
        'accordion-header flex items-center justify-start text-left gap-4 w-full cursor-pointer overflow-hidden text-blue-700 absolute top-0 bottom-0 px-8 sm:px-10'
    );

    const twCard = cn(
        'grid w-full grid-rows-[0fr] p-8 pb-12 transition-all duration-300 overflow-hidden sm:px-10'
    );
    const headerRefs = useRef({});
    const contentRefs = useRef({});

    useEffect(() => {
        if (!open) return;

        const twContent = ['grid-rows-[1fr]', 'pb-8'];
        const twShowHeader = ['opacity-100', 'transition-all', 'duration-100'];
        const twHiddenHeader = ['opacity-0', 'pointer-events-none'];

        Object.keys(contentRefs.current).forEach((key) => {
            const isCurrent = key === open;
            const header = headerRefs.current[key];
            const content = contentRefs.current[key];

            if (isCurrent) {
                header.classList.add(...twHiddenHeader);
                header.classList.remove(...twShowHeader);
                content.classList.add(...twContent);

                return;
            }

            content.classList.remove(...twContent);
            setTimeout(() => {
                header.classList.remove(...twHiddenHeader);
                header.classList.add(...twShowHeader);
            }, 300);
        });
    }, [open]);

    return (
        <div className="container flex flex-col gap-4 overflow-y-hidden">
            {feature?.map((feature) => {
                const { headline, icon, identifier, category } = feature;
                const twColor = Object.values(category?.[0] || {})[0];

                return (
                    <div key={identifier} className={`${twClassesAccordion} ${twColor}`}>
                        <div
                            data-header={identifier}
                            className={twHeader}
                            onClick={() => setOpen(identifier)}
                            ref={(el) => (headerRefs.current[identifier] = el)}>
                            {icon && (
                                <div className="relative size-10 sm:size-12 md:size-16">
                                    <Image
                                        src={icon?.idPath ?? icon}
                                        alt="icon"
                                        fill={true}
                                        className="object-contain"
                                        sizes="48px"
                                    />
                                </div>
                            )}
                            <h4 className="flex-1 truncate text-2xl">{headline}</h4>
                            <IconCircleArrowUpRight className="size-8 sm:size-10 md:size-12" />
                        </div>
                        <div
                            data-content={identifier}
                            ref={(el) => (contentRefs.current[identifier] = el)}
                            className={twCard}>
                            <div className={'overflow-y-hidden'}>
                                <DotCard {...feature} />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Layout02({ featuretablecells, tableHeader }) {
    return (
        <>
            <FeatureTable {...tableHeader} />
            {featuretablecells?.map((item, index) => (
                <div key={index} className="border-b border-gray-300">
                    <AccordionItem tableHeader={tableHeader} item={item} />
                </div>
            ))}
        </>
    );
}

// OLD accordion layout based on a column with rows of features and another column with the image
// eslint-disable-next-line no-unused-vars
function Layout03_old({ feature }) {
    const [hoveredIndex, setHoveredIndex] = useState(0);

    const featuresToRender = createFeaturesToRender(feature);
    const currentImage = getImageUrl(featuresToRender[hoveredIndex]?.image);

    const handleItemClick = (idx) => {
        const url = featuresToRender[idx].url;
        if (url) location.href = url;
    };

    if (!featuresToRender.length) return null;

    return (
        <div className="container flex w-full flex-col items-center justify-between gap-8 bg-transparent md:flex-row">
            <div className="flex flex-1 flex-col gap-4">
                {featuresToRender.map((item, idx) => (
                    <div
                        key={item.id}
                        className={`group flex flex-col rounded-xl bg-custom-lightGray p-4 shadow-sm transition-all duration-200 md:flex-row md:items-center md:justify-between md:px-8 md:py-5 ${
                            item.url ? 'cursor-pointer hover:bg-gray-200' : 'cursor-default'
                        }`}
                        onClick={() => handleItemClick(idx)}
                        onMouseEnter={() => setHoveredIndex(idx)}
                        role={item.url ? 'button' : 'article'}
                        aria-label={`${item.url ? 'Visit demo for' : 'View details for'} ${item.title}`}>
                        <div className="flex-1">
                            {item.title && (
                                <div className="mb-2 text-xl font-semibold text-gray-700">
                                    {item.title}
                                </div>
                            )}
                            {item.body && (
                                <DotBlockEditor
                                    blocks={item.body?.json ?? item.body}
                                    customRenderers={{
                                        listItem: ListItem,
                                        paragraph: Paragraph
                                    }}
                                />
                            )}
                        </div>
                        {item.url && (
                            <IconArrowNarrowRight
                                className="ml-2 mt-2 text-fuschia-600 transition-transform duration-200 group-hover:translate-x-1 md:mt-0"
                                size={22}
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-8 hidden min-h-[320px] flex-1 items-center justify-center md:mt-0 md:flex">
                {currentImage && (
                    <Image
                        src={currentImage}
                        alt={`${featuresToRender[hoveredIndex]?.title || 'Accordion'} illustration`}
                        quality={55}
                        width={320}
                        height={320}
                        className="h-auto w-full object-contain transition-opacity duration-200"
                    />
                )}
            </div>
        </div>
    );
}

function ListItemLayout04(props) {
    const { node } = props;
    if (!node?.content) return null;

    const [item] = node?.content;
    const { content: itemContent } = item;
    const [{ text }] = itemContent;

    return (
        <li className="mb-4 flex items-start gap-1 text-[18px]">
            <IconCircleCheck size={24} className="mr-2 shrink-0 text-[#bf27d3]" />
            {decode(text)}
        </li>
    );
}

function Layout04({ feature }) {
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
    const buttonRefs = useRef([]);

    const featuresToRender = createFeaturesToRender(feature);
    const titles = featuresToRender.map((item) => item.title);

    useEffect(() => {
        const updateIndicator = () => {
            const activeButton = buttonRefs.current[selectedCategoryIndex];
            if (activeButton) {
                // Use offsetLeft and offsetWidth for more accurate measurements
                setIndicatorStyle({
                    left: activeButton.offsetLeft,
                    width: activeButton.offsetWidth
                });
            }
        };

        // Small delay to ensure layout is complete
        const timeoutId = setTimeout(updateIndicator, 0);

        window.addEventListener('resize', updateIndicator);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updateIndicator);
        };
    }, [selectedCategoryIndex]);

    return (
        <>
            {titles.length > 0 && (
                <div className="mb-6 hidden w-full justify-center md:flex">
                    <div className="relative inline-flex items-center gap-1 rounded-full bg-blue-100 p-1.5">
                        <div
                            className="absolute rounded-full bg-fuschia-700 shadow-sm transition-all duration-150 ease-in"
                            style={{
                                left: `${indicatorStyle.left}px`,
                                width: `${indicatorStyle.width}px`,
                                top: '6px',
                                bottom: '6px'
                            }}
                        />
                        {titles.map((key, index) => (
                            <button
                                key={key}
                                ref={(el) => (buttonRefs.current[index] = el)}
                                onClick={() => setSelectedCategoryIndex(index)}
                                className={cn(
                                    'relative z-10 rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-200 sm:text-base',
                                    selectedCategoryIndex === index
                                        ? 'text-white'
                                        : 'text-blue-700 hover:bg-blue-200'
                                )}>
                                {titles[index]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="hidden min-h-[550px] flex-col items-center justify-around gap-8 bg-transparent px-4 md:flex md:flex-row">
                <div className="relative flex min-h-[550px] flex-[0_0_50%] flex-col items-start gap-4">
                    {featuresToRender.map((feature, index) => (
                        <div
                            key={feature.id}
                            className={cn(
                                'absolute inset-0 flex flex-col w-full rounded-xl p-4 justify-center transition-opacity duration-300',
                                selectedCategoryIndex === index
                                    ? 'opacity-100'
                                    : 'opacity-0 pointer-events-none'
                            )}
                            aria-label={`${feature.url ? 'Visit demo for' : 'Select'} ${feature.title}`}
                            style={{ top: '-15px' }}>
                            {feature.title && (
                                <h3 className="mb-4 text-2xl font-bold text-black lg:text-3xl">
                                    {feature.title}
                                </h3>
                            )}
                            {[feature.body, feature.afterBody].map((blocks, idx) =>
                                blocks ? (
                                    <div key={idx}>
                                        <DotBlockEditor
                                            blocks={blocks}
                                            customRenderers={{
                                                listItem: ({ node }) =>
                                                    ListItemLayout04({
                                                        node,
                                                        specialStyle: true
                                                    }),
                                                paragraph: ({ node }) =>
                                                    Paragraph({
                                                        node,
                                                        specialStyle: false,
                                                        customStyle:
                                                            'text-[18px] text-muted-foreground mb-8 font-semibold'
                                                    })
                                            }}
                                        />
                                    </div>
                                ) : null
                            )}
                            {/* <CallToActionButton {...feature.callToAction?.[0]} /> */}
                        </div>
                    ))}
                </div>

                <div className="relative flex min-h-[550px] flex-[0_0_50%] items-center justify-end md:mt-0">
                    {featuresToRender.map((feature, index) => {
                        const imageUrl = getImageUrl(feature?.image);
                        if (!imageUrl) return null;

                        return (
                            <div
                                key={`image-${index}`}
                                className={cn(
                                    'absolute inset-0 flex items-center justify-end transition-opacity duration-300',
                                    selectedCategoryIndex === index
                                        ? 'opacity-100'
                                        : 'opacity-0 pointer-events-none'
                                )}>
                                <Image
                                    src={imageUrl}
                                    alt={`${feature?.title || 'Feature'} illustration`}
                                    quality={55}
                                    width={320}
                                    height={320}
                                    priority={index === 0}
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    className="h-auto w-full object-contain"
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex w-full flex-col gap-4 md:hidden">
                {featuresToRender.map((item) => (
                    <div
                        key={item.id}
                        className={`group flex cursor-pointer flex-col rounded-xl p-4 transition-all duration-200`}
                        role="button"
                        aria-label={`${item.url ? 'Visit demo for' : 'Select'} ${item.title}`}>
                        <div className="flex-1">
                            {item.title && (
                                <div className="mb-4 text-2xl font-semibold text-gray-700 sm:text-3xl">
                                    {item.title}
                                </div>
                            )}
                            <div className="mb-4">
                                {item.body && (
                                    <DotBlockEditor
                                        blocks={item.body}
                                        customRenderers={{
                                            listItem: ListItem,
                                            paragraph: Paragraph
                                        }}
                                    />
                                )}
                            </div>
                            <div className="flex w-full justify-center">
                                <Image
                                    src={getImageUrl(item?.image)}
                                    alt={`${item?.title || 'Feature'} illustration`}
                                    quality={55}
                                    width={280}
                                    height={280}
                                    className="object-contain"
                                    style={{ width: 'auto', height: 'auto', maxWidth: '100%' }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

// Interactive Wheel Layout
function Layout03({ feature }) {
    const [active, setActive] = useState(0);
    const featuresToRender = createFeaturesToRender(feature);

    if (!featuresToRender.length) return null;

    const activeFeature = featuresToRender[active];
    const activeImage = getImageUrl(activeFeature?.image, 600);

    // Calculate node position around circle
    const getNodePosition = (index, total) => {
        const angle = (index * (360 / total) - 90) * (Math.PI / 180);
        const radius = 32.5;
        return {
            x: 50 + Math.cos(angle) * radius,
            y: 50 + Math.sin(angle) * radius
        };
    };

    return (
        <section className="overflow-hidden bg-transparent py-12 lg:py-20">
            <div className="container mx-auto px-4">
                <div className="mx-auto max-w-6xl">
                    <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16">
                        {/* Wheel */}
                        <div className="relative mx-auto aspect-square w-full max-w-md">
                            {/* Rings */}
                            <div className="absolute inset-0 rounded-full border border-gray-300/50" />
                            <div className="absolute inset-4 rounded-full border border-gray-300/30" />

                            {/* Lines */}
                            <svg className="absolute inset-0 z-0 size-full" viewBox="0 0 400 400">
                                {featuresToRender.map((_, i) => {
                                    const angle =
                                        (i * (360 / featuresToRender.length) - 90) *
                                        (Math.PI / 180);
                                    const x = Math.cos(angle) * 130 + 200;
                                    const y = Math.sin(angle) * 130 + 200;
                                    const isActive = active === i;

                                    return (
                                        <line
                                            key={i}
                                            x1="200"
                                            y1="200"
                                            x2={x}
                                            y2={y}
                                            className={
                                                isActive
                                                    ? 'stroke-fuschia-600'
                                                    : 'stroke-gray-300/40'
                                            }
                                            strokeWidth={isActive ? 2 : 1}
                                        />
                                    );
                                })}
                            </svg>

                            {/* Nodes */}
                            {featuresToRender.map((item, i) => {
                                const { x, y } = getNodePosition(i, featuresToRender.length);
                                const isActive = active === i;
                                const nodeImage = getImageUrl(item?.image, 200);

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => setActive(i)}
                                        className="group absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
                                        style={{ left: `${x}%`, top: `${y}%` }}>
                                        <div
                                            className={cn(
                                                'relative h-16 w-16 overflow-hidden rounded-full transition-all duration-500 lg:h-20 lg:w-20',
                                                isActive &&
                                                    'shadow-xl shadow-fuschia-600/20 ring-4 ring-fuschia-600 ring-offset-2 ring-offset-white',
                                                !isActive &&
                                                    'ring-2 ring-gray-300 hover:scale-105 hover:ring-fuschia-600/50'
                                            )}>
                                            {nodeImage && (
                                                <div
                                                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                                    style={{ backgroundImage: `url(${nodeImage})` }}
                                                />
                                            )}
                                        </div>
                                        <span
                                            className={cn(
                                                'absolute left-1/2 mt-2 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-gray-600 transition-opacity duration-300',
                                                'opacity-0 group-hover:opacity-100',
                                                isActive && 'opacity-0'
                                            )}>
                                            {item.title?.split(' ')[0]}
                                        </span>
                                    </button>
                                );
                            })}

                            {/* Center */}
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <div className="flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-fuschia-600 to-fuschia-700 shadow-lg shadow-fuschia-600/30 lg:size-24">
                                    <Shield className="text-white lg:size-10" />
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="text-center lg:text-left">
                            {/* Image */}
                            {activeImage && (
                                <div className="relative mx-auto mb-6 h-56 max-w-[490px] overflow-hidden rounded-2xl shadow-lg shadow-fuschia-600/10 ring-1 ring-fuchsia-100 lg:mx-0 lg:h-72 lg:max-w-[560px]">
                                    {featuresToRender.map((item, i) => {
                                        const itemImage = getImageUrl(item?.altImage, 600);
                                        if (!itemImage) return null;

                                        return (
                                            <div
                                                key={item.id}
                                                className={cn(
                                                    'absolute inset-0 bg-cover bg-center transition-all duration-700',
                                                    active === i
                                                        ? 'scale-100 opacity-100'
                                                        : 'scale-105 opacity-0'
                                                )}
                                                style={{
                                                    backgroundImage: `url(${itemImage})`
                                                }}></div>
                                        );
                                    })}

                                    {activeFeature.label && (
                                        <div className="absolute bottom-4 left-4 rounded-full bg-fuschia-700 px-3 py-1 text-xs font-semibold text-white">
                                            {activeFeature.label}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Text */}
                            <div className="space-y-4">
                                {activeFeature.title && (
                                    <h3 className="text-2xl font-bold lg:text-3xl">
                                        {activeFeature.title}
                                    </h3>
                                )}

                                {activeFeature.body && (
                                    <div className="mx-auto !mt-1 max-w-md lg:mx-0">
                                        <DotBlockEditor
                                            blocks={activeFeature.body}
                                            customRenderers={{
                                                listItem: ListItem,
                                                paragraph: Paragraph
                                            }}
                                        />
                                    </div>
                                )}

                                {activeFeature.url && (
                                    <LinkNoPrefetch
                                        href={activeFeature.url}
                                        className="group/btn hover:bg-fuschia-50 mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:border-fuschia-600">
                                        <span>Learn more</span>
                                        <IconArrowNarrowRight className="transition-transform group-hover/btn:translate-x-1" />
                                    </LinkNoPrefetch>
                                )}
                            </div>

                            {/* Dots */}
                            <div className="mt-8 flex items-center justify-center gap-2 lg:justify-start">
                                {featuresToRender.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActive(i)}
                                        className={cn(
                                            'h-2 rounded-full transition-all duration-300',
                                            active === i
                                                ? 'w-8 bg-fuschia-600'
                                                : 'w-2 bg-gray-300 hover:bg-fuschia-600/50'
                                        )}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const layoutComponents = {
    1: Layout01,
    2: Layout02,
    3: Layout03,
    4: Layout04
    // 5: Layout05
};

function Accordion({ tableHeader, featuretablecells, feature, layout = 1 }) {
    const LayoutComponent = layoutComponents[layout] || Layout01;
    return (
        <LayoutComponent
            tableHeader={tableHeader}
            featuretablecells={featuretablecells}
            feature={feature}
        />
    );
}

export default Accordion;
